import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Save,
  Download,
  ChevronDown,
  RotateCcw,
  Upload,
  Trash2,
  Image,
  Palette,
  Square,
  Circle,
  Eye,
  Frame,
  Type,
  Sparkles,
  Grid3X3,
  CircleDot,
  ArrowLeft,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import QRCodeStyling from 'qr-code-styling';
import { ImagePicker } from '@/components/ImagePicker';
import { apiV1, extractErrorMessage } from '@/api/client';

// Every call here goes through apiV1, not raw fetch. SSO users hold only the
// linkhub_session cookie, so a hand-built `Bearer ${localStorage...}` header
// became "Bearer null" (and 'auth_token' is a key nothing ever sets). The
// server rejects that with 401 before it looks at the cookie.
const errorMessage = (error, fallback) =>
  extractErrorMessage(error?.response?.data?.detail) || error?.message || fallback;

// Pattern style options supported by qr-code-styling library
// Note: qr-code-styling library supports 6 pattern types (fewer than QR.io's 18+)
const PATTERN_STYLES = [
  { value: 'square', label: 'Square' },
  { value: 'dots', label: 'Dots' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'extra-rounded', label: 'Extra Rounded' },
  { value: 'classy', label: 'Classy' },
  { value: 'classy-rounded', label: 'Classy Rounded' },
];

// Corner square (outer eye) styles - all options from qr-code-styling
const CORNER_SQUARE_STYLES = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'extra-rounded', label: 'Extra Rounded' },
  { value: 'dots', label: 'Dots' },
  { value: 'classy', label: 'Classy' },
  { value: 'classy-rounded', label: 'Classy Rounded' },
];

// Corner dot (inner eye) styles - all options from qr-code-styling
const CORNER_DOT_STYLES = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'extra-rounded', label: 'Extra Rounded' },
  { value: 'dots', label: 'Dots' },
  { value: 'classy', label: 'Classy' },
  { value: 'classy-rounded', label: 'Classy Rounded' },
];

// Frame styles
const FRAME_STYLES = [
  { value: 'none', label: 'No Frame' },
  { value: 'bottom', label: 'Bottom Banner' },
  { value: 'top', label: 'Top Banner' },
  { value: 'box', label: 'Full Box' },
];

// Preset colour palettes
const COLOUR_PRESETS = [
  { name: 'Classic', fg: '#000000', bg: '#ffffff' },
  { name: 'Ocean', fg: '#2AA4BF', bg: '#ffffff' },
  { name: 'Forest', fg: '#228B22', bg: '#ffffff' },
  { name: 'Berry', fg: '#8B008B', bg: '#ffffff' },
  { name: 'Sunset', fg: '#FF6B35', bg: '#ffffff' },
  { name: 'Night', fg: '#1a2733', bg: '#f0f4f8' },
  { name: 'Teal', fg: '#008080', bg: '#ffffff' },
  { name: 'Corporate', fg: '#1F8CA6', bg: '#ffffff' },
];

const defaultOptions = {
  width: 300,
  height: 300,
  data: 'https://example.com',
  type: 'svg',
  dotsOptions: {
    color: '#1a2733',
    type: 'square',
    gradient: null,
  },
  cornersSquareOptions: {
    color: '#1a2733',
    type: 'square',
  },
  cornersDotOptions: {
    color: '#1a2733',
    type: 'square',
  },
  backgroundOptions: {
    color: '#ffffff',
  },
  imageOptions: {
    crossOrigin: 'anonymous',
    margin: 10,
    imageSize: 0.4,
    hideBackgroundDots: true,
  },
  qrOptions: {
    errorCorrectionLevel: 'M',
  },
};

// Pattern Preview Component
function PatternPreview({ type, isSelected, onClick }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            data-testid={`pattern-${type}`}
            className={`
              w-14 h-14 rounded-lg border-2 flex items-center justify-center
              transition-all duration-150 hover:scale-105
              ${isSelected 
                ? 'border-primary bg-primary/10 shadow-md' 
                : 'border-border bg-card hover:border-primary/50'
              }
            `}
          >
            <div className={`w-8 h-8 ${getPatternPreviewClass(type)}`} />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="capitalize">{type.replace('-', ' ')}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function getPatternPreviewClass(type) {
  const baseStyle = 'bg-foreground';
  switch (type) {
    case 'square':
      return `${baseStyle} rounded-none`;
    case 'dots':
      return `${baseStyle} rounded-full`;
    case 'rounded':
      return `${baseStyle} rounded-md`;
    case 'extra-rounded':
      return `${baseStyle} rounded-xl`;
    case 'classy':
      return `${baseStyle} rounded-sm rotate-45 scale-75`;
    case 'classy-rounded':
      return `${baseStyle} rounded-lg rotate-45 scale-75`;
    default:
      return baseStyle;
  }
}

// Colour Input Component with preview
function ColourInput({ label, value, onChange, testId }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex gap-2">
        <div className="relative">
          <Input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-12 h-10 p-1 cursor-pointer border-2 rounded-lg"
            data-testid={testId}
          />
        </div>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="flex-1 font-mono text-sm"
        />
      </div>
    </div>
  );
}

// Gradient Editor Component
function GradientEditor({ enabled, gradient, onChange, onToggle }) {
  const [gradientType, setGradientType] = useState(gradient?.type || 'linear');
  const [colour1, setColour1] = useState(gradient?.colorStops?.[0]?.color || '#2AA4BF');
  const [colour2, setColour2] = useState(gradient?.colorStops?.[1]?.color || '#5FD2B5');
  const [rotation, setRotation] = useState(gradient?.rotation || 0);

  // Memoize the update function without dependencies that cause loops
  const applyGradient = useCallback(() => {
    onChange({
      type: gradientType,
      rotation: gradientType === 'linear' ? rotation : undefined,
      colorStops: [
        { offset: 0, color: colour1 },
        { offset: 1, color: colour2 },
      ],
    });
  }, [gradientType, colour1, colour2, rotation, onChange]);

  // Only apply gradient when values change AND gradient is enabled
  useEffect(() => {
    if (enabled) {
      applyGradient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradientType, colour1, colour2, rotation]);
  
  // Handle initial enable
  useEffect(() => {
    if (enabled && !gradient) {
      applyGradient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Enable Gradient</Label>
        <Switch
          checked={enabled}
          onCheckedChange={onToggle}
          data-testid="gradient-toggle"
        />
      </div>

      {enabled && (
        <>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Gradient Type</Label>
            <Select
              value={gradientType}
              onValueChange={(v) => {
                setGradientType(v);
              }}
            >
              <SelectTrigger data-testid="gradient-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="linear">Linear</SelectItem>
                <SelectItem value="radial">Radial</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <ColourInput
              label="Start Colour"
              value={colour1}
              onChange={setColour1}
              testId="gradient-colour-1"
            />
            <ColourInput
              label="End Colour"
              value={colour2}
              onChange={setColour2}
              testId="gradient-colour-2"
            />
          </div>

          {gradientType === 'linear' && (
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label className="text-xs text-muted-foreground">Angle</Label>
                <span className="text-xs text-muted-foreground">{rotation}°</span>
              </div>
              <Slider
                value={[rotation]}
                onValueChange={([v]) => setRotation(v)}
                min={0}
                max={360}
                step={15}
                data-testid="gradient-rotation-slider"
              />
            </div>
          )}

          {/* Gradient Preview */}
          <div 
            className="h-8 rounded-lg border"
            style={{
              background: gradientType === 'linear'
                ? `linear-gradient(${rotation}deg, ${colour1}, ${colour2})`
                : `radial-gradient(circle, ${colour1}, ${colour2})`
            }}
            data-testid="gradient-preview"
          />
        </>
      )}
    </div>
  );
}

// Logo Upload Component
function LogoUploader({ logoUrl, onLogoChange, onLogoRemove, logoOptions, onLogoOptionsChange, qrCodeId }) {
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo file must be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        onLogoChange(event.target.result);
        toast.success('Logo uploaded successfully');
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Determine the folder path for QR code assets
  const qrFolder = qrCodeId ? `qr_codes/${qrCodeId}` : 'qr_codes';

  return (
    <div className="space-y-4">
      {/* Image Picker with folder organization */}
      <ImagePicker
        label="QR Code Logo"
        value={logoUrl || ''}
        onChange={(url) => onLogoChange(url)}
        placeholder="Select or upload a logo for your QR code"
        aspectRatio="1/1"
        folder={qrFolder}
        testId="qr-logo-picker"
      />
      
      {logoUrl && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onLogoRemove}
          className="text-destructive hover:text-destructive w-full"
          data-testid="logo-remove-button"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Remove Logo
        </Button>
      )}

      {/* Legacy: Direct file upload option */}
      <div className="pt-2 border-t">
        <p className="text-xs text-muted-foreground mb-2">Or upload directly:</p>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            data-testid="logo-upload-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload File
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      </div>

      {/* Logo URL Input */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Or paste image URL</Label>
        <Input
          value={logoUrl || ''}
          onChange={(e) => onLogoChange(e.target.value)}
          placeholder="https://example.com/logo.png"
          data-testid="logo-url-input"
        />
      </div>

      {/* Logo Preview & Options */}
      {logoUrl && (
        <div className="p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center justify-center mb-3">
            <img
              src={logoUrl}
              alt="Logo preview"
              className="max-w-24 max-h-24 object-contain rounded"
              data-testid="logo-preview"
            />
          </div>

          {/* Logo Size */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label className="text-xs text-muted-foreground">Size</Label>
              <span className="text-xs text-muted-foreground">{Math.round(logoOptions.imageSize * 100)}%</span>
            </div>
            <Slider
              value={[logoOptions.imageSize * 100]}
              onValueChange={([v]) => onLogoOptionsChange({ ...logoOptions, imageSize: v / 100 })}
              min={10}
              max={50}
              step={5}
              data-testid="logo-size-slider"
            />
          </div>

          {/* Logo Margin */}
          <div className="space-y-2 mt-3">
            <div className="flex justify-between">
              <Label className="text-xs text-muted-foreground">Margin</Label>
              <span className="text-xs text-muted-foreground">{logoOptions.margin}px</span>
            </div>
            <Slider
              value={[logoOptions.margin]}
              onValueChange={([v]) => onLogoOptionsChange({ ...logoOptions, margin: v })}
              min={0}
              max={30}
              step={2}
              data-testid="logo-margin-slider"
            />
          </div>

          {/* Hide Background Dots */}
          <div className="flex items-center justify-between mt-4">
            <Label className="text-xs text-muted-foreground">Hide dots behind logo</Label>
            <Switch
              checked={logoOptions.hideBackgroundDots}
              onCheckedChange={(v) => onLogoOptionsChange({ ...logoOptions, hideBackgroundDots: v })}
              data-testid="logo-hide-dots-toggle"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Frame Editor Component (CSS-based frame around QR)
function FrameEditor({ frameStyle, frameText, frameColour, frameBgColour, onStyleChange, onTextChange, onColourChange, onBgColourChange }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Frame Style</Label>
        <div className="grid grid-cols-2 gap-2">
          {FRAME_STYLES.map((style) => (
            <Button
              key={style.value}
              variant={frameStyle === style.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => onStyleChange(style.value)}
              className="justify-start"
              data-testid={`frame-style-${style.value}`}
            >
              <Frame className="h-4 w-4 mr-2" />
              {style.label}
            </Button>
          ))}
        </div>
      </div>

      {frameStyle !== 'none' && (
        <>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Frame Text</Label>
            <Input
              value={frameText}
              onChange={(e) => onTextChange(e.target.value)}
              placeholder="SCAN ME"
              maxLength={30}
              data-testid="frame-text-input"
            />
          </div>

          <ColourInput
            label="Text Colour"
            value={frameColour}
            onChange={onColourChange}
            testId="frame-text-colour"
          />

          <ColourInput
            label="Background Colour"
            value={frameBgColour}
            onChange={onBgColourChange}
            testId="frame-bg-colour"
          />
        </>
      )}
    </div>
  );
}

export default function VisualEditor() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qrRef = useRef(null);
  const qrCodeRef = useRef(null);
  const [options, setOptions] = useState(defaultOptions);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [gradientEnabled, setGradientEnabled] = useState(false);
  
  // Frame state
  const [frameStyle, setFrameStyle] = useState('none');
  const [frameText, setFrameText] = useState('SCAN ME');
  const [frameColour, setFrameColour] = useState('#ffffff');
  const [frameBgColour, setFrameBgColour] = useState('#1a2733');
  
  // Template save dialog state
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateCategory, setTemplateCategory] = useState('custom');
  
  // Check context - where did we come from?
  const fromWizard = searchParams.get('from') === 'wizard';
  const fromTemplates = searchParams.get('from') === 'templates';
  const mode = searchParams.get('mode'); // 'create' for new template, 'edit' for editing
  const templateId = searchParams.get('template'); // template ID if editing
  const qrId = searchParams.get('qr'); // QR code ID if editing existing QR
  const returnStep = searchParams.get('step') || '4';
  
  // State for editing existing QR code
  const [editingQR, setEditingQR] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);

  // Load existing QR code if editing
  useEffect(() => {
    if (qrId) {
      const loadQRCode = async () => {
        setLoadingQR(true);
        try {
          const { data } = await apiV1.get(`/qrcodes/${qrId}`);
          if (data.success && data.data) {
            const qr = data.data;
            setEditingQR(qr);
            
            // Set the content URL - use qr_url (tracking URL) so scans are tracked
            setOptions(prev => ({
              ...prev,
              data: qr.qr_url || qr.destination_url || prev.data,
            }));
            
            // Apply design from QR code if it has custom design
            if (qr.design) {
              const design = qr.design;
              setOptions(prev => ({
                ...prev,
                dotsOptions: {
                  ...prev.dotsOptions,
                  type: design.dots_options?.type || prev.dotsOptions.type,
                  color: design.dots_options?.color || prev.dotsOptions.color,
                  gradient: design.dots_options?.gradient || null,
                },
                cornersSquareOptions: {
                  ...prev.cornersSquareOptions,
                  type: design.corners_square_options?.type || prev.cornersSquareOptions.type,
                  color: design.corners_square_options?.color || prev.cornersSquareOptions.color,
                },
                cornersDotOptions: {
                  ...prev.cornersDotOptions,
                  type: design.corners_dot_options?.type || prev.cornersDotOptions.type,
                  color: design.corners_dot_options?.color || prev.cornersDotOptions.color,
                },
                backgroundOptions: {
                  color: design.background_options?.color || prev.backgroundOptions.color,
                },
              }));
              
              if (design.image_options?.src) {
                setLogoUrl(design.image_options.src);
              }
              if (design.dots_options?.gradient) {
                setGradientEnabled(true);
              }
            }
          }
        } catch (error) {
          console.error('Error loading QR code:', error);
          toast.error(errorMessage(error, 'Failed to load QR code'));
        } finally {
          setLoadingQR(false);
        }
      };
      loadQRCode();
    }
  }, [qrId]);

  // Load saved wizard state if coming from wizard
  useEffect(() => {
    if (fromWizard) {
      const savedState = sessionStorage.getItem('qr_wizard_design');
      if (savedState) {
        try {
          const design = JSON.parse(savedState);
          // Apply saved design to options
          if (design.dotsOptions) {
            setOptions(prev => ({
              ...prev,
              dotsOptions: { ...prev.dotsOptions, ...design.dotsOptions },
              cornersSquareOptions: design.cornersSquareOptions || prev.cornersSquareOptions,
              cornersDotOptions: design.cornersDotOptions || prev.cornersDotOptions,
              backgroundOptions: design.backgroundOptions || prev.backgroundOptions,
            }));
          }
          if (design.logoUrl) setLogoUrl(design.logoUrl);
          if (design.frameStyle) setFrameStyle(design.frameStyle);
          if (design.frameText) setFrameText(design.frameText);
          if (design.frameColour) setFrameColour(design.frameColour);
          if (design.frameBgColour) setFrameBgColour(design.frameBgColour);
          if (design.gradientEnabled) setGradientEnabled(design.gradientEnabled);
        } catch (e) {
          console.error('Failed to load wizard design state:', e);
        }
      }
    }
  }, [fromWizard]);

  // Load template data if editing an existing template
  useEffect(() => {
    if (templateId) {
      const loadTemplate = async () => {
        try {
          const { data } = await apiV1.get(`/qrcodes/templates/${templateId}`);
          if (data.success && data.data) {
            const template = data.data;
            setTemplateName(template.name || '');
            setTemplateDescription(template.description || '');
            setTemplateCategory(template.category || 'custom');
            
            // Apply design from template
            if (template.design) {
              const design = template.design;
              setOptions(prev => ({
                ...prev,
                dotsOptions: design.dots_options || prev.dotsOptions,
                cornersSquareOptions: design.corners_square_options || prev.cornersSquareOptions,
                cornersDotOptions: design.corners_dot_options || prev.cornersDotOptions,
                backgroundOptions: design.background_options || prev.backgroundOptions,
              }));
              
              // Load logo from image_options (new format) or logo_url (legacy)
              if (design.image_options?.image_url) {
                setLogoUrl(design.image_options.image_url);
              } else if (design.logo_url) {
                setLogoUrl(design.logo_url);
              }
              
              // Load frame options (new format) or legacy fields
              if (design.frame_options) {
                if (design.frame_options.style) setFrameStyle(design.frame_options.style);
                if (design.frame_options.text) setFrameText(design.frame_options.text);
                if (design.frame_options.text_color) setFrameColour(design.frame_options.text_color);
                if (design.frame_options.background_color) setFrameBgColour(design.frame_options.background_color);
              } else {
                // Legacy format
                if (design.frame_style) setFrameStyle(design.frame_style);
                if (design.frame_text) setFrameText(design.frame_text);
                if (design.frame_colour) setFrameColour(design.frame_colour);
                if (design.frame_bg_colour) setFrameBgColour(design.frame_bg_colour);
              }
            }
          }
        } catch (error) {
          console.error('Error loading template:', error);
          toast.error('Failed to load template');
        }
      };
      loadTemplate();
    }
  }, [templateId]);

  // Handle applying design and returning to wizard
  const handleApplyAndReturn = () => {
    // Save the current design to sessionStorage
    const designState = {
      dotsOptions: options.dotsOptions,
      cornersSquareOptions: options.cornersSquareOptions,
      cornersDotOptions: options.cornersDotOptions,
      backgroundOptions: options.backgroundOptions,
      imageOptions: options.imageOptions,
      logoUrl,
      frameStyle,
      frameText,
      frameColour,
      frameBgColour,
      gradientEnabled,
    };
    sessionStorage.setItem('qr_wizard_design', JSON.stringify(designState));
    toast.success('Design saved! Returning to QR creation...');
    navigate(`/qrs/new?step=${returnStep}&designApplied=true`);
  };

  // Handle cancel and return to wizard
  const handleCancelAndReturn = () => {
    navigate(`/qrs/new?step=${returnStep}`);
  };

  // Handle cancel and return to templates
  const handleCancelToTemplates = () => {
    navigate('/templates');
  };

  // Handle saving template
  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setSaving(true);
    try {
      // Build image_options if logo is set
      const imageOptions = logoUrl ? {
        image_url: logoUrl,
        image_size: 0.3,
        margin: 10,
        hide_background_dots: true,
      } : null;
      
      // Build frame_options if frame is set
      const frameOptions = frameStyle !== 'none' ? {
        style: frameStyle,
        text: frameText,
        text_color: frameColour,
        background_color: frameBgColour,
      } : null;
      
      const templateData = {
        name: templateName.trim(),
        description: templateDescription.trim() || null,
        category: templateCategory,
        design: {
          dots_options: options.dotsOptions,
          corners_square_options: options.cornersSquareOptions,
          corners_dot_options: options.cornersDotOptions,
          background_options: options.backgroundOptions,
          image_options: imageOptions,
          frame_options: frameOptions,
        },
        is_public: true,
      };

      const { data } = templateId
        ? await apiV1.put(`/qrcodes/templates/${templateId}`, templateData)
        : await apiV1.post('/qrcodes/templates/', templateData);
      if (data.success) {
        toast.success(templateId ? 'Template updated!' : 'Template created!');
        setSaveTemplateDialogOpen(false);
        navigate('/templates');
      } else {
        toast.error('Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(errorMessage(error, 'Failed to save template'));
    } finally {
      setSaving(false);
    }
  };

  // Initialize QR code
  useEffect(() => {
    if (!qrCodeRef.current) {
      qrCodeRef.current = new QRCodeStyling(options);
    }
  }, []);

  // Update QR code when options change
  useEffect(() => {
    if (qrCodeRef.current && qrRef.current) {
      qrRef.current.innerHTML = '';
      
      const updatedOptions = { ...options };
      if (logoUrl) {
        updatedOptions.image = logoUrl;
        // Increase error correction when logo is present
        updatedOptions.qrOptions = {
          ...updatedOptions.qrOptions,
          errorCorrectionLevel: 'H',
        };
      } else {
        updatedOptions.image = undefined;
      }
      
      qrCodeRef.current.update(updatedOptions);
      qrCodeRef.current.append(qrRef.current);
    }
  }, [options, logoUrl]);

  const updateOption = (path, value) => {
    setOptions((prev) => {
      const newOptions = JSON.parse(JSON.stringify(prev)); // Deep clone
      const keys = path.split('.');
      let current = newOptions;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newOptions;
    });
  };

  const handleDownload = async (format) => {
    if (qrCodeRef.current) {
      await qrCodeRef.current.download({
        name: 'qr-code',
        extension: format,
      });
      toast.success(`QR Code downloaded as ${format.toUpperCase()}`);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    // TODO: Save to database
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setSaving(false);
    toast.success('Design saved successfully!');
  };

  // Save design to existing QR code
  const handleSaveQRDesign = async () => {
    if (!qrId) return;
    
    setSaving(true);
    try {
      // Build the design object matching the backend schema
      const designPayload = {
        design: {
          dots_options: {
            type: options.dotsOptions.type,
            color: options.dotsOptions.color,
            gradient: gradientEnabled ? options.dotsOptions.gradient : null,
          },
          corners_square_options: {
            type: options.cornersSquareOptions.type,
            color: options.cornersSquareOptions.color,
          },
          corners_dot_options: {
            type: options.cornersDotOptions.type,
            color: options.cornersDotOptions.color,
          },
          background_options: {
            color: options.backgroundOptions.color,
          },
          image_options: logoUrl ? {
            src: logoUrl,
            margin: options.imageOptions?.margin || 10,
          } : null,
        },
      };
      
      const { data } = await apiV1.put(`/qrcodes/${qrId}`, designPayload);

      if (data.success) {
        toast.success('QR code design updated successfully!');
        navigate(`/qrs/${qrId}`);
      } else {
        throw new Error('Failed to save design');
      }
    } catch (error) {
      console.error('Error saving QR design:', error);
      toast.error('Failed to save design: ' + errorMessage(error, 'unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setOptions(defaultOptions);
    setLogoUrl('');
    setGradientEnabled(false);
    setFrameStyle('none');
    setFrameText('SCAN ME');
    setFrameColour('#ffffff');
    setFrameBgColour('#1a2733');
    toast.info('Design reset to defaults');
  };

  const handleGradientChange = (gradient) => {
    updateOption('dotsOptions.gradient', gradient);
  };

  const handleGradientToggle = (enabled) => {
    setGradientEnabled(enabled);
    if (!enabled) {
      updateOption('dotsOptions.gradient', null);
    }
  };

  const applyColourPreset = (preset) => {
    // Apply preset to all elements
    updateOption('dotsOptions.color', preset.fg);
    updateOption('cornersSquareOptions.color', preset.fg);
    updateOption('cornersDotOptions.color', preset.fg);
    updateOption('backgroundOptions.color', preset.bg);
    toast.success(`Applied "${preset.name}" colour preset`);
  };

  // Get the appropriate title and description based on context
  const getTitle = () => {
    if (fromWizard) return "Customize QR Design";
    if (qrId && editingQR) return `Edit Design: ${editingQR.name}`;
    if (qrId) return "Edit QR Design";
    if (fromTemplates && mode === 'create') return "Create Template";
    if (templateId) return "Edit Template";
    return "Visual Editor";
  };

  const getDescription = () => {
    if (fromWizard) return "Customize your QR code design, then apply it to continue creating your QR code";
    if (qrId) return "Update the design of your existing QR code";
    if (fromTemplates && mode === 'create') return "Design a reusable QR code template";
    if (templateId) return `Editing: ${templateName || 'Template'}`;
    return "Customise your QR code design with patterns, colours, logos, and frames";
  };

  return (
    <div className="space-y-4" data-testid="visual-editor-page">
      <PageHeader
        title={getTitle()}
        description={getDescription()}
        actions={
          <div className="flex items-center gap-2">
            {/* Back button for templates mode */}
            {(fromTemplates || templateId) && !qrId && (
              <Button variant="ghost" size="sm" onClick={handleCancelToTemplates} data-testid="back-to-templates-button">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Templates
              </Button>
            )}
            
            {/* Back button for QR code editing mode */}
            {qrId && (
              <Button variant="ghost" size="sm" onClick={() => navigate(`/qrs/${qrId}`)} data-testid="back-to-qr-button">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to QR Code
              </Button>
            )}
            
            {/* Cancel button for wizard mode */}
            {fromWizard && (
              <Button variant="ghost" size="sm" onClick={handleCancelAndReturn} data-testid="cancel-return-button">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleReset} data-testid="reset-button">
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            
            {/* Wizard mode: Apply & Continue */}
            {fromWizard ? (
              <Button size="sm" onClick={handleApplyAndReturn} data-testid="apply-return-button">
                <Check className="h-4 w-4 mr-2" />
                Apply Design & Continue
              </Button>
            ) : qrId ? (
              /* QR Code edit mode: Save Design */
              <Button size="sm" onClick={handleSaveQRDesign} disabled={saving} data-testid="save-qr-design-button">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Design'}
              </Button>
            ) : (fromTemplates || templateId) ? (
              /* Template mode: Save Template */
              <Button size="sm" onClick={() => setSaveTemplateDialogOpen(true)} disabled={saving} data-testid="save-template-button">
                <Save className="h-4 w-4 mr-2" />
                {templateId ? 'Update Template' : 'Save Template'}
              </Button>
            ) : (
              /* Default mode: Save & Download */
              <>
                <Button variant="outline" size="sm" onClick={() => setSaveTemplateDialogOpen(true)} data-testid="save-as-template-button">
                  <Save className="h-4 w-4 mr-2" />
                  Save as Template
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button data-testid="qr-visual-download-button">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                      <ChevronDown className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleDownload('svg')} data-testid="download-svg">
                      SVG (Vector)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDownload('png')} data-testid="download-png">
                      PNG (High Quality)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDownload('jpeg')} data-testid="download-jpeg">
                      JPEG
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDownload('webp')} data-testid="download-webp">
                      WebP
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        }
      />

      {/* Save Template Dialog */}
      {saveTemplateDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSaveTemplateDialogOpen(false)}>
          <div className="bg-background rounded-lg p-6 w-full max-w-md shadow-lg" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">
              {templateId ? 'Update Template' : 'Save as Template'}
            </h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="template-name">Template Name *</Label>
                <Input
                  id="template-name"
                  placeholder="e.g., Modern Blue"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label htmlFor="template-description">Description</Label>
                <Input
                  id="template-description"
                  placeholder="Brief description of the template"
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="template-category">Category</Label>
                <select
                  id="template-category"
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  value={templateCategory}
                  onChange={(e) => setTemplateCategory(e.target.value)}
                >
                  <option value="custom">Custom</option>
                  <option value="business">Business</option>
                  <option value="marketing">Marketing</option>
                  <option value="events">Events</option>
                  <option value="social">Social</option>
                  <option value="real_estate">Real Estate</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setSaveTemplateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveTemplate} disabled={saving || !templateName.trim()}>
                  {saving ? 'Saving...' : (templateId ? 'Update' : 'Save Template')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ResizablePanelGroup
        direction="horizontal"
        className="min-h-[650px] border rounded-lg overflow-hidden"
      >
        {/* Controls Panel */}
        <ResizablePanel defaultSize={38} minSize={30} className="bg-card" data-testid="qr-visual-editor-controls">
          <ScrollArea className="h-full">
            <div className="p-4">
              <Tabs defaultValue="pattern" className="w-full">
                <TabsList className="w-full grid grid-cols-5 mb-4">
                  <TabsTrigger value="pattern" className="text-xs px-2">
                    <Square className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Pattern</span>
                  </TabsTrigger>
                  <TabsTrigger value="eyes" className="text-xs px-2">
                    <Eye className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Eyes</span>
                  </TabsTrigger>
                  <TabsTrigger value="colours" className="text-xs px-2">
                    <Palette className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Colours</span>
                  </TabsTrigger>
                  <TabsTrigger value="logo" className="text-xs px-2">
                    <Image className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Logo</span>
                  </TabsTrigger>
                  <TabsTrigger value="frame" className="text-xs px-2">
                    <Frame className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Frame</span>
                  </TabsTrigger>
                </TabsList>

                {/* Pattern Tab */}
                <TabsContent value="pattern" className="space-y-6 mt-0">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        Pattern Style
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-2">
                        {PATTERN_STYLES.map((style) => (
                          <PatternPreview
                            key={style.value}
                            type={style.value}
                            isSelected={options.dotsOptions.type === style.value}
                            onClick={() => updateOption('dotsOptions.type', style.value)}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Type className="h-4 w-4 text-primary" />
                        Content URL
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Input
                        value={options.data}
                        onChange={(e) => updateOption('data', e.target.value)}
                        placeholder="https://example.com"
                        data-testid="qr-content-url"
                      />
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Eyes Tab */}
                <TabsContent value="eyes" className="space-y-6 mt-0">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Corner Square (Outer Eye)</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <Select
                        value={options.cornersSquareOptions.type}
                        onValueChange={(value) => updateOption('cornersSquareOptions.type', value)}
                      >
                        <SelectTrigger data-testid="corner-square-style">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CORNER_SQUARE_STYLES.map((style) => (
                            <SelectItem key={style.value} value={style.value}>
                              {style.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Corner Dot (Inner Eye)</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <Select
                        value={options.cornersDotOptions.type}
                        onValueChange={(value) => updateOption('cornersDotOptions.type', value)}
                      >
                        <SelectTrigger data-testid="corner-dot-style">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CORNER_DOT_STYLES.map((style) => (
                            <SelectItem key={style.value} value={style.value}>
                              {style.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Colours Tab - Now with granular control */}
                <TabsContent value="colours" className="space-y-6 mt-0">
                  {/* Colour Presets */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Quick Presets</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-2">
                        {COLOUR_PRESETS.map((preset) => (
                          <TooltipProvider key={preset.name}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => applyColourPreset(preset)}
                                  className="w-full aspect-square rounded-lg border-2 hover:border-primary transition-colors overflow-hidden"
                                  style={{ backgroundColor: preset.bg }}
                                  data-testid={`preset-${preset.name.toLowerCase()}`}
                                >
                                  <div
                                    className="w-full h-full flex items-center justify-center"
                                    style={{ color: preset.fg }}
                                  >
                                    <Square className="h-6 w-6" fill={preset.fg} />
                                  </div>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{preset.name}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Background Colour */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Background</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ColourInput
                        label="Background Colour"
                        value={options.backgroundOptions.color}
                        onChange={(v) => updateOption('backgroundOptions.color', v)}
                        testId="background-colour"
                      />
                    </CardContent>
                  </Card>

                  {/* Inner Pattern Colour */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Grid3X3 className="h-4 w-4 text-primary" />
                        Inner Pattern
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ColourInput
                        label="Pattern Colour"
                        value={options.dotsOptions.color}
                        onChange={(v) => updateOption('dotsOptions.color', v)}
                        testId="pattern-colour"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        The main dots/modules that form the QR code data
                      </p>
                    </CardContent>
                  </Card>

                  {/* Eye (Corner Square) Colour */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Eye className="h-4 w-4 text-primary" />
                        Eye (Outer)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ColourInput
                        label="Eye Colour"
                        value={options.cornersSquareOptions.color}
                        onChange={(v) => updateOption('cornersSquareOptions.color', v)}
                        testId="eye-colour"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        The outer square of the three corner markers
                      </p>
                    </CardContent>
                  </Card>

                  {/* Dot (Corner Dot) Colour */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <CircleDot className="h-4 w-4 text-primary" />
                        Dot (Inner)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ColourInput
                        label="Dot Colour"
                        value={options.cornersDotOptions.color}
                        onChange={(v) => updateOption('cornersDotOptions.color', v)}
                        testId="dot-colour"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        The inner dot of the three corner markers
                      </p>
                    </CardContent>
                  </Card>

                  {/* Gradient */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Palette className="h-4 w-4 text-primary" />
                        Gradient (Pattern)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <GradientEditor
                        enabled={gradientEnabled}
                        gradient={options.dotsOptions.gradient}
                        onChange={handleGradientChange}
                        onToggle={handleGradientToggle}
                      />
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Logo Tab */}
                <TabsContent value="logo" className="space-y-6 mt-0">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Image className="h-4 w-4 text-primary" />
                        Logo Integration
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <LogoUploader
                        logoUrl={logoUrl}
                        onLogoChange={setLogoUrl}
                        onLogoRemove={() => setLogoUrl('')}
                        logoOptions={options.imageOptions}
                        onLogoOptionsChange={(opts) => updateOption('imageOptions', opts)}
                        qrCodeId={qrId}
                      />
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Error Correction</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Higher correction allows for logos but increases QR density. 
                          {logoUrl && ' Auto-set to High for logo support.'}
                        </p>
                        <Select
                          value={logoUrl ? 'H' : (options.qrOptions?.errorCorrectionLevel || 'M')}
                          onValueChange={(value) => updateOption('qrOptions.errorCorrectionLevel', value)}
                          disabled={!!logoUrl}
                        >
                          <SelectTrigger data-testid="error-correction-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="L">Low (7%)</SelectItem>
                            <SelectItem value="M">Medium (15%)</SelectItem>
                            <SelectItem value="Q">Quartile (25%)</SelectItem>
                            <SelectItem value="H">High (30%)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Frame Tab */}
                <TabsContent value="frame" className="space-y-6 mt-0">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Frame className="h-4 w-4 text-primary" />
                        Frame Design
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <FrameEditor
                        frameStyle={frameStyle}
                        frameText={frameText}
                        frameColour={frameColour}
                        frameBgColour={frameBgColour}
                        onStyleChange={setFrameStyle}
                        onTextChange={setFrameText}
                        onColourChange={setFrameColour}
                        onBgColourChange={setFrameBgColour}
                      />
                    </CardContent>
                  </Card>

                  {/* QR Size */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">QR Code Size</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <Label className="text-xs text-muted-foreground">Size</Label>
                          <span className="text-xs text-muted-foreground">{options.width}px</span>
                        </div>
                        <Slider
                          value={[options.width]}
                          onValueChange={([value]) => {
                            updateOption('width', value);
                            updateOption('height', value);
                          }}
                          min={150}
                          max={500}
                          step={25}
                          data-testid="qr-size-slider"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </ScrollArea>
        </ResizablePanel>

        <ResizableHandle withHandle className="data-[state=active]:bg-primary" />

        {/* Preview Panel */}
        <ResizablePanel defaultSize={62} className="bg-muted/30" data-testid="qr-visual-preview-pane">
          <div className="h-full flex flex-col">
            <div className="flex-1 flex items-center justify-center p-8">
              {/* Frame wrapper */}
              <div 
                className={`
                  relative transition-all duration-200
                  ${frameStyle !== 'none' ? 'p-0' : ''}
                `}
              >
                {/* Top Frame Banner */}
                {(frameStyle === 'top' || frameStyle === 'box') && (
                  <div 
                    className="text-center py-3 px-6 rounded-t-xl font-bold text-sm tracking-wide"
                    style={{ backgroundColor: frameBgColour, color: frameColour }}
                    data-testid="frame-top-banner"
                  >
                    {frameText}
                  </div>
                )}

                {/* QR Card */}
                <Card 
                  className={`
                    bg-card shadow-lg
                    ${frameStyle === 'top' ? 'rounded-t-none' : ''}
                    ${frameStyle === 'bottom' ? 'rounded-b-none' : ''}
                    ${frameStyle === 'box' ? 'rounded-none' : ''}
                  `}
                  style={frameStyle === 'box' ? { 
                    borderLeft: `4px solid ${frameBgColour}`,
                    borderRight: `4px solid ${frameBgColour}`,
                  } : {}}
                >
                  <CardContent className="p-8">
                    <div ref={qrRef} data-testid="qr-visual-preview" className="flex items-center justify-center" />
                  </CardContent>
                </Card>

                {/* Bottom Frame Banner */}
                {(frameStyle === 'bottom' || frameStyle === 'box') && (
                  <div 
                    className="text-center py-3 px-6 rounded-b-xl font-bold text-sm tracking-wide"
                    style={{ backgroundColor: frameBgColour, color: frameColour }}
                    data-testid="frame-bottom-banner"
                  >
                    {frameText}
                  </div>
                )}
              </div>
            </div>

            {/* Export Options Footer */}
            <div className="border-t bg-card p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Export Format
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleDownload('svg')} data-testid="export-svg">
                    SVG
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDownload('png')} data-testid="export-png">
                    PNG
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDownload('jpeg')} data-testid="export-jpeg">
                    JPEG
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
